import React, { createContext, useEffect, useState, useRef } from 'react';
import {
  addItemToCart,
  updateCartItemQty,
  removeCartItem,
  fetchCart,
  clearCart as clearCartApi,
} from '../api/cartService';
import useAuth from '../hooks/useAuth';

export const OrderContext = createContext();

const OrderProvider = ({ children }) => {
  const [orders, setOrders] = useState([]);
  const { user } = useAuth();
  const debounceTimers = useRef({});

  const transformItems = (items = []) => {
    const now = new Date();

    return items
      .filter((item) => item.product && typeof item.product === 'object')
      .map((item) => {
        const { product, quantity } = item;
        const {
          _id,
          name,
          image,
          initialPrice,
          promoPercentage,
          promoStartDate,
          promoEndDate,
        } = product;

        const promoStart = promoStartDate ? new Date(promoStartDate) : null;
        const promoEnd = promoEndDate ? new Date(promoEndDate) : null;

        const isPromoActive =
          promoPercentage &&
          promoPercentage > 0 &&
          promoStart &&
          now >= promoStart &&
          (!promoEnd || now <= promoEnd);

        const price = isPromoActive
          ? Number((initialPrice * (1 - promoPercentage / 100)).toFixed(2))
          : initialPrice;

        return {
          id: _id,
          name,
          image,
          price,
          quantity,
          ...(isPromoActive && {
            originalPrice: initialPrice,
            promoPercentage,
          }),
        };
      });
  };

  const refreshCart = () => {
    return fetchCart()
      .then((cart) => setOrders(transformItems(cart?.items)))
      .catch(() => setOrders([]));
  };

  const clearCart = () => {
    clearCartApi().then(refreshCart).catch(() => {});
  };

  useEffect(() => {
    if (!user) return;
    refreshCart();
  }, [user]);

  const handleCart = (product) => {
    addItemToCart(product._id, product.quantity || 1)
      .then(refreshCart)
      .catch(() => {});
  };

  const incrementQty = (id) => {
    const item = orders.find((i) => i.id === id);
    if (!item) return;
    const newQty = item.quantity + 1;

    setOrders((prev) =>
      prev.map((o) => (o.id === id ? { ...o, quantity: newQty } : o))
    );

    debounceUpdateQty(id, newQty);
  };

  const decrementQty = (id) => {
    const item = orders.find((i) => i.id === id);
    if (!item) return;

    const newQty = item.quantity - 1;

    if (newQty <= 0) {
      removeCartItem(id).then(refreshCart).catch(() => {});
    } else {
      setOrders((prev) =>
        prev.map((o) => (o.id === id ? { ...o, quantity: newQty } : o))
      );
      debounceUpdateQty(id, newQty);
    }
  };

  const debounceUpdateQty = (id, quantity) => {
    if (debounceTimers.current[id]) {
      clearTimeout(debounceTimers.current[id]);
    }

    debounceTimers.current[id] = setTimeout(() => {
      updateCartItemQty(id, quantity).then(refreshCart).catch(() => {});
    }, 500);
  };

  const updateQtyDirect = (id, quantity) => {
    updateCartItemQty(id, quantity).then(refreshCart).catch(() => {});
  };

    const removeProduct = (id) => {
      removeCartItem(id).then(refreshCart).catch(() => {});
    };
    const removeItemsFromCartByIds = async (ids = []) => {
    for (const id of ids) {
      try {
        await removeCartItem(id);
      } catch (err) {
        console.error(`Erreur lors de la suppression de l'article ${id}`, err);
      }
    }
    refreshCart();
  };


  const value = {
    orders,
    handleCart,
    removeProduct,
    incrementQty,
    decrementQty,
    updateQtyDirect,
    refreshCart,
    clearCart,
    removeItemsFromCartByIds,
  };
  

  return (
    <OrderContext.Provider value={value}>
      {children}
    </OrderContext.Provider>
  );
};

export default OrderProvider;
